import React, { useEffect, useState } from 'react';
import { API_BASE } from '../shared/constants';

type Locale = 'hr' | 'en';
const GOLD = 'var(--ad-acc)';
const KEYS: string[] = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'back'];

/**
 * Dijeljeni ekran za potvrdu čitanja edukacije/okružnice PIN-om.
 * ?ack=edu_subject|circular&id=ID — više ljudi na jednom linku, svatko ukuca PIN.
 */
export function AckScreen({ locale }: { locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
    const subjectType = params.get('ack') || '';
    const subjectId = params.get('id') || '';

    const [title, setTitle] = useState('');
    const [pin, setPin] = useState('');
    const [busy, setBusy] = useState(false);
    const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

    useEffect(() => {
        fetch(`${API_BASE}/ack/meta?subject_type=${encodeURIComponent(subjectType)}&subject_id=${encodeURIComponent(subjectId)}`)
            .then((r) => r.json()).then((d) => setTitle(d?.title ?? '')).catch(() => undefined);
    }, [subjectType, subjectId]);

    const press = (k: string) => {
        if (busy) return;
        setFeedback(null);
        if (k === 'clear') { setPin(''); return; }
        if (k === 'back') { setPin((p) => p.slice(0, -1)); return; }
        if (pin.length >= 6) return;
        setPin((p) => p + k);
    };

    const submit = async () => {
        if (busy || pin.length < 4) return;
        setBusy(true); setFeedback(null);
        try {
            const res = await fetch(`${API_BASE}/ack/pin`, {
                method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
                body: JSON.stringify({ subject_type: subjectType, subject_id: Number(subjectId), pin }),
            });
            const d = await res.json().catch(() => ({}));
            setPin('');
            if (!d.ok) { setFeedback({ kind: 'err', text: t('Neispravan ili istekao PIN.', 'Invalid or expired PIN.') }); return; }
            setFeedback({ kind: 'ok', text: d.already
                ? t(`${d.employee_first_name}, već je evidentirano.`, `${d.employee_first_name}, already recorded.`)
                : t(`Evidentirano — ${d.employee_first_name}, hvala!`, `Recorded — ${d.employee_first_name}, thanks!`) });
        } catch { setFeedback({ kind: 'err', text: t('Greška veze.', 'Connection error.') }); }
        finally { setBusy(false); }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'linear-gradient(160deg,#2b2410,#0f0c05)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#fff', padding: 24, boxSizing: 'border-box', fontFamily: 'system-ui, sans-serif' }}>
            <div style={{ marginBottom: 16, textAlign: 'center', maxWidth: 460 }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: GOLD }}>{t('Potvrda čitanja', 'Confirm read')}</div>
                {title ? <div style={{ fontSize: '1.05rem', color: '#e7d9b0', marginTop: 6 }}>{title}</div> : null}
                <div style={{ fontSize: '0.9rem', color: '#d8c79a', marginTop: 6 }}>{t('Unesi svoj PIN', 'Enter your PIN')}</div>
            </div>
            <div style={{ fontSize: '2.2rem', letterSpacing: '0.5rem', fontFamily: 'monospace', minHeight: '2.6rem', marginBottom: 6, minWidth: 200, textAlign: 'center' }}>
                {pin ? '•'.repeat(pin.length) : <span style={{ color: '#6b5a30' }}>-----</span>}
            </div>
            <div style={{ minHeight: 26, marginBottom: 10 }}>
                {feedback ? <div style={{ fontSize: '1.05rem', fontWeight: 600, color: feedback.kind === 'ok' ? '#7fd47f' : '#ff8a8a' }}>{feedback.text}</div> : null}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 84px)', gap: 12 }}>
                {KEYS.map((k) => {
                    const isAction = k === 'clear' || k === 'back';
                    const label = k === 'clear' ? 'C' : k === 'back' ? '⌫' : k;
                    return <button key={k} type="button" onClick={() => press(k)} disabled={busy} style={{ width: 84, height: 84, borderRadius: 16, fontSize: '1.7rem', fontWeight: 600, cursor: busy ? 'default' : 'pointer', border: '1px solid rgba(var(--ad-acc-rgb),0.5)', background: isAction ? 'rgba(var(--ad-acc-rgb),0.18)' : 'rgba(255,255,255,0.08)', color: isAction ? GOLD : '#fff' }}>{label}</button>;
                })}
            </div>
            <button type="button" onClick={submit} disabled={busy || pin.length < 4} style={{ marginTop: 20, width: 84 * 3 + 24, height: 60, borderRadius: 16, fontSize: '1.15rem', fontWeight: 700, border: 'none', cursor: busy || pin.length < 4 ? 'default' : 'pointer', background: busy || pin.length < 4 ? '#5a4a22' : GOLD, color: busy || pin.length < 4 ? '#9a8a55' : '#2b2410' }}>
                {busy ? t('Šaljem…', 'Sending…') : t('Potvrdi', 'Confirm')}
            </button>
        </div>
    );
}
